import DeleteIcon from '@mui/icons-material/Delete'
import EventIcon from '@mui/icons-material/Event'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material'
import React from 'react'

import db from '../../db/db'
import Database from '../../types/Database'
import { generateColorFromString } from '../../utils/FormatHelpers'

interface MyBookingsListProps {
  bookings: Database.Booking[]
  onBookingDeleted: () => void
  currentUserId: string
}

export default function MyBookingsList({
  bookings,
  onBookingDeleted,
  currentUserId
}: MyBookingsListProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [bookingToDelete, setBookingToDelete] =
    React.useState<Database.Booking | null>(null)

  // Filter and sort future bookings
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const futureBookings = bookings
    .filter(booking => {
      if (!booking.forDate) return false

      // Handle Firestore Timestamp objects
      let bookingDate: Date
      if (
        booking.forDate &&
        typeof booking.forDate === 'object' &&
        'seconds' in booking.forDate
      ) {
        // Firestore Timestamp - convert seconds to milliseconds
        bookingDate = new Date((booking.forDate as any).seconds * 1000)
      } else {
        bookingDate = new Date(booking.forDate)
      }

      bookingDate.setHours(0, 0, 0, 0)
      return bookingDate >= today
    })
    .sort((a, b) => {
      if (!a.forDate || !b.forDate) return 0

      let dateA: number
      let dateB: number

      if (typeof a.forDate === 'object' && 'seconds' in a.forDate) {
        dateA = (a.forDate as any).seconds * 1000
      } else {
        dateA = new Date(a.forDate).getTime()
      }

      if (typeof b.forDate === 'object' && 'seconds' in b.forDate) {
        dateB = (b.forDate as any).seconds * 1000
      } else {
        dateB = new Date(b.forDate).getTime()
      }

      return dateA - dateB
    })

  const handleDeleteClick = (booking: Database.Booking) => {
    setBookingToDelete(booking)
    setDeleteDialogOpen(true)
  }

  const canDeleteBooking = (booking: Database.Booking): boolean => {
    // Allow deletion if current user is the booking owner OR the booking creator
    const isOwner = booking.bookingOwnerId === currentUserId
    const isCreator = booking.bookingCreatorId === currentUserId

    // For legacy bookings without bookingOwnerId, check uid
    const isLegacyOwner =
      !booking.bookingOwnerId && booking.uid === currentUserId

    return isOwner || isCreator || isLegacyOwner
  }

  const handleConfirmDelete = async () => {
    if (!bookingToDelete) return

    try {
      if (!bookingToDelete.id) return
      await db.deleteItem('booking', bookingToDelete.id)
      setDeleteDialogOpen(false)
      setBookingToDelete(null)
      onBookingDeleted()
    } catch (error) {
      console.error('Error deleting booking:', error)
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setBookingToDelete(null)
  }

  const getOfficeColor = (officeName: string | undefined) => {
    return generateColorFromString(officeName || 'default')
  }

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Unknown date'

    // Handle Firestore Timestamp objects
    let jsDate: Date
    if (typeof date === 'object' && 'seconds' in date) {
      jsDate = new Date((date as any).seconds * 1000)
    } else {
      jsDate = new Date(date)
    }

    return jsDate.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <Box>
      {/* Bookings List */}
      {futureBookings.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant='h6' color='text.secondary' gutterBottom>
              No upcoming bookings
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              You don't have any desk bookings scheduled
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List sx={{ width: '100%' }}>
          {futureBookings.map((booking, index) => {
            const officeColor = getOfficeColor(booking.officeName || 'default')
            return (
              <Card
                key={booking.id || index}
                sx={{
                  mb: 1.5,
                  '&:hover': {
                    boxShadow: 2
                  }
                }}
              >
                <ListItem
                  sx={{
                    py: 1.5,
                    px: 2
                  }}
                  secondaryAction={
                    canDeleteBooking(booking) ? (
                      <IconButton
                        edge='end'
                        aria-label='delete'
                        onClick={() => handleDeleteClick(booking)}
                        sx={{
                          color: 'error.main',
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.dark'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    ) : null
                  }
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant='subtitle1'
                        component='div'
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {formatDate(booking.forDate)}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={booking.officeName || 'Unknown Office'}
                          size='small'
                          sx={{
                            backgroundColor: 'transparent',
                            border: `2px solid ${officeColor}`,
                            color: 'text.primary',
                            fontWeight: 600,
                            height: 24,
                            '&::before': {
                              content: '""',
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: officeColor,
                              marginRight: '6px'
                            }
                          }}
                        />
                        <Chip
                          label={booking.deskName || 'Unknown'}
                          size='small'
                          variant='outlined'
                          sx={{ height: 24 }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              </Card>
            )
          })}
        </List>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby='delete-dialog-title'
        aria-describedby='delete-dialog-description'
      >
        <DialogTitle id='delete-dialog-title'>Delete Booking?</DialogTitle>
        <DialogContent>
          <DialogContentText id='delete-dialog-description'>
            Are you sure you want to delete this booking?
            {bookingToDelete && (
              <Box sx={{ mt: 2 }}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  Date: {formatDate(bookingToDelete.forDate)}
                </Typography>
                <Typography variant='body2'>
                  Office: {bookingToDelete.officeName}
                </Typography>
                <Typography variant='body2'>
                  Desk: {bookingToDelete.deskName}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color='error'
            variant='contained'
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
